import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { LocalizedName } from '@protocols/domain';

import { MethodTypeSelectorItem } from '../component/method-types/method-type-selector/method-type-selector.component';

export type PipelineCategoryVariant = 'basic' | 'options';
type PipelineCategoryStatus = 'idle' | 'saved' | 'cancelled';
export type PendingPersistence = 'save' | 'update' | undefined;

export interface PipelineCategoryOption {
  uuid: string;
  name: LocalizedName;
  thrdis: number;
  pendingPersistence: PendingPersistence;
}

export interface PipelineCategoryInitialInput {
  methodType: MethodTypeSelectorItem | null;
  variant?: PipelineCategoryVariant;
  readonly?: boolean;
  /**
   * Whether label colours are relevant to the step this session edits —
   * resolved by the opener (the category editor never learns environment or
   * task). Omitted means supported, so existing openers are unaffected.
   */
  supportsLabelColors?: boolean;
  initialCategory?: {
    uuid: string;
    name: LocalizedName;
    pendingPersistence: PendingPersistence;
    options: PipelineCategoryOption[];
  };
}

export interface PipelineCategoryResult {
  category: { uuid: string; name: LocalizedName; pendingPersistence: PendingPersistence };
  options: PipelineCategoryOption[];
  methodTypeId?: string;
}

interface PipelineCategoryStateModel {
  methodType: MethodTypeSelectorItem | null;
  variant: PipelineCategoryVariant;
  readonly: boolean;
  supportsLabelColors: boolean;
  category: { uuid: string; name: LocalizedName; pendingPersistence: PendingPersistence } | null;
  categoryInput: string;
  options: PipelineCategoryOption[];
  optionInput: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  status: PipelineCategoryStatus;
  result: PipelineCategoryResult | null;
  /**
   * Colour each label carries on the session's method type, as persisted at
   * session initialize (label uuid → colour). Fetched once per session by the
   * effects — persisting happens after editors close, so the slice cannot go
   * stale mid-session. Empty when the fetch failed, was skipped, or found
   * nothing; the swatch preview then falls back to the palette icon.
   */
  persistedLabelColors: Record<string, string>;
  /*
   * Token that identifies an editing session.
   * It is necessary to avoid losing pending labels when
   * we start editing a new category
   */
  sessionToken: number;
  /**
   * Signature of the buffer as of the last baselined seed
   * (`PipelineCategoryEffects.initialize`), or `null` when the session has no
   * baseline (direct `initialize`/`reset` on the store). The facade's
   * `hasChanges$` compares this against the live signature; a `null` baseline
   * always reads as clean. Lives inside `INITIAL_STATE` spreads, so any
   * re-seed/reset auto-clears it.
   */
  baselineSignature: string | null;
}

const INITIAL_STATE: PipelineCategoryStateModel = {
  methodType: null,
  variant: 'basic',
  readonly: false,
  supportsLabelColors: true,
  category: null,
  categoryInput: '',
  options: [],
  optionInput: '',
  loading: false,
  saving: false,
  error: null,
  status: 'idle',
  result: null,
  persistedLabelColors: {},
  sessionToken: 0,
  baselineSignature: null,
};

@Injectable()
export class PipelineCategoryState extends ComponentStore<PipelineCategoryStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly methodType$ = this.select((state) => state.methodType);
  public readonly variant$ = this.select((state) => state.variant);
  public readonly readonly$ = this.select((state) => state.readonly);
  public readonly supportsLabelColors$ = this.select((state) => state.supportsLabelColors);
  public readonly category$ = this.select((state) => state.category);
  public readonly categoryName$ = this.select((state) => state.category?.name ?? null);
  public readonly categoryInput$ = this.select((state) => state.categoryInput);
  public readonly options$ = this.select((state) => state.options);
  public readonly optionInput$ = this.select((state) => state.optionInput);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly saving$ = this.select((state) => state.saving);
  public readonly error$ = this.select((state) => state.error);
  public readonly status$ = this.select((state) => state.status);
  public readonly result$ = this.select((state) => state.result);
  public readonly persistedLabelColors$ = this.select((state) => state.persistedLabelColors);
  public readonly sessionToken$ = this.select((state) => state.sessionToken);
  public readonly baselineSignature$ = this.select((state) => state.baselineSignature);

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly initialize = this.updater((state, input: PipelineCategoryInitialInput) => ({
    ...INITIAL_STATE,
    sessionToken: state.sessionToken + 1,
    methodType: input.methodType,
    variant: input.variant ?? INITIAL_STATE.variant,
    readonly: input.readonly ?? INITIAL_STATE.readonly,
    supportsLabelColors: input.supportsLabelColors ?? INITIAL_STATE.supportsLabelColors,
    category: input.initialCategory
      ? {
          uuid: input.initialCategory.uuid,
          name: input.initialCategory.name,
          pendingPersistence: input.initialCategory.pendingPersistence,
        }
      : null,
    options: input.initialCategory ? [...input.initialCategory.options] : [],
  }));

  public readonly reset = this.updater((state) => ({ ...INITIAL_STATE, sessionToken: state.sessionToken + 1 }));

  public readonly setVariant = this.updater((state, variant: PipelineCategoryVariant) => ({
    ...state,
    variant,
  }));

  public readonly setReadonly = this.updater((state, readonly: boolean) => ({ ...state, readonly }));

  public readonly setCategoryInput = this.updater((state, categoryInput: string) => ({
    ...state,
    categoryInput,
  }));

  public readonly setCategoryFromLabel = this.updater(
    (state, category: { uuid: string; name: LocalizedName; pendingPersistence: PendingPersistence }) => ({
      ...state,
      category,
      categoryInput: '',
    })
  );

  public readonly setOptionInput = this.updater((state, optionInput: string) => ({
    ...state,
    optionInput,
  }));

  public readonly addOption = this.updater((state, option: PipelineCategoryOption) => {
    if (state.options.some((existing) => existing.uuid === option.uuid)) return state;
    return {
      ...state,
      options: [...state.options, option],
      optionInput: '',
    };
  });

  public readonly removeOption = this.updater((state, uuid: string) => ({
    ...state,
    options: state.options.filter((option) => option.uuid !== uuid),
  }));

  public readonly updateOptionThrdis = this.updater((state, payload: { uuid: string; thrdis: number }) => ({
    ...state,
    options: state.options.map((option) =>
      option.uuid === payload.uuid ? { ...option, thrdis: payload.thrdis } : option
    ),
  }));

  public readonly updateOptionName = this.updater((state, payload: { uuid: string; name: LocalizedName }) => ({
    ...state,
    options: state.options.map((option) =>
      option.uuid === payload.uuid
        ? {
            ...option,
            name: payload.name,
            pendingPersistence: option.pendingPersistence ?? 'update',
          }
        : option
    ),
  }));

  public readonly updateCategoryName = this.updater((state, name: LocalizedName) => ({
    ...state,
    category: state.category
      ? { ...state.category, name, pendingPersistence: state.category.pendingPersistence ?? 'update' }
      : state.category,
  }));

  public readonly replaceOptions = this.updater((state, options: PipelineCategoryOption[]) => ({
    ...state,
    options,
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  public readonly setError = this.updater((state, error: string | null) => ({ ...state, error }));

  public readonly setStatus = this.updater((state, status: PipelineCategoryStatus) => ({
    ...state,
    status,
  }));

  public readonly setResult = this.updater((state, result: PipelineCategoryResult | null) => ({
    ...state,
    result,
  }));

  public readonly setPersistedLabelColors = this.updater((state, persistedLabelColors: Record<string, string>) => ({
    ...state,
    persistedLabelColors,
  }));

  public readonly setBaselineSignature = this.updater((state, baselineSignature: string) => ({
    ...state,
    baselineSignature,
  }));
}
